import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Zap, Star, Trophy, Shield, GraduationCap, Target,
  TrendingUp, Award, Briefcase, Users, CheckCircle,
  ArrowRight, Sparkles, Medal, Crown, ChevronRight } from
'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function GameGuideDialog({ open, onClose }) {
  const [simulatedXP, setSimulatedXP] = useState(450);
  const [simulatedSP, setSimulatedSP] = useState(0);
  const [simulatedLevel, setSimulatedLevel] = useState(1);
  const [activeSimulation, setActiveSimulation] = useState(null);

  const simulateActivityCompletion = () => {
    setActiveSimulation('activity');
    setTimeout(() => {
      setSimulatedXP((prev) => prev + 500);
      setSimulatedSP((prev) => prev + 50);
      setTimeout(() => setActiveSimulation(null), 1000);
    }, 500);
  };

  const simulateLevelUp = () => {
    setActiveSimulation('levelup');
    setTimeout(() => {
      setSimulatedLevel((prev) => prev + 1);
      setTimeout(() => setActiveSimulation(null), 1000);
    }, 500);
  };

  const resetSimulation = () => {
    setSimulatedXP(450);
    setSimulatedSP(0);
    setSimulatedLevel(1);
  };

  const currentLevelXP = simulatedXP % 1000;
  const progressPercent = currentLevelXP / 1000 * 100;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="profile-gameguidedialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            Welcome to COMPASS: Your Officer's Guide
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="grid w-full grid-cols-5 bg-slate-950 border-slate-800">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="progression">Progression</TabsTrigger>
            <TabsTrigger value="skills">Skills</TabsTrigger>
            <TabsTrigger value="ranks">Ranks & Roles</TabsTrigger>
            <TabsTrigger value="simulation">Try It</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            <Card className="bg-slate-950/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-slate-100 text-lg font-semibold tracking-tight flex items-center gap-2">
                  <Target className="w-5 h-5 text-indigo-400" />
                  The COMPASS System
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-300">
                  COMPASS is a gamified operational system where you grow as an officer through 
                  completing missions, developing skills, and earning recognition. Your journey 
                  is tracked through three core currencies:
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                          <Zap className="w-5 h-5 text-amber-400" />
                        </div>
                        <h4 className="font-bold text-amber-400">Experience Points (XP)</h4>
                      </div>
                      <p className="text-sm text-slate-300">
                        Earned by completing <strong>direct tasks</strong>—work that directly advances mission objectives. 
                        XP determines your overall level and unlocks new capabilities.
                      </p>
                      <div className="mt-4 p-3 bg-slate-950/50 rounded-lg">
                        <div className="text-slate-200 text-xs">Ways to earn:</div>
                        <ul className="text-sm text-slate-300 mt-2 space-y-1">
                          <li>• Complete direct tasks (mission-advancing work)</li>
                          <li>• Finish objectives: 200-500 XP</li>
                          <li>• Complete missions: 500-1000 XP</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-emerald-400" />
                        </div>
                        <h4 className="font-bold text-emerald-400">Enabling Points (EP)</h4>
                      </div>
                      <p className="text-sm text-slate-300">
                        Earned by completing <strong>indirect tasks</strong>—work that enables or unblocks direct tasks 
                        (research, setup, coordination). EP converts to XP when the linked direct task is completed.
                      </p>
                      <div className="mt-4 p-3 bg-slate-950/50 rounded-lg">
                        <div className="text-slate-200 text-xs">How it works:</div>
                        <ul className="text-sm text-slate-300 mt-2 space-y-1">
                          <li>• Complete indirect/enabling tasks</li>
                          <li>• EP accumulates until linked task completes</li>
                          <li>• Converts to XP automatically</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/30">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                          <GraduationCap className="w-5 h-5 text-cyan-400" />
                        </div>
                        <h4 className="font-bold text-cyan-400">Skill Points (SP)</h4>
                      </div>
                      <p className="text-sm text-slate-300">
                        Specialized currency for developing specific skills. SPs are awarded for 
                        activities requiring those skills and advance your proficiency.
                      </p>
                      <div className="mt-4 p-3 bg-slate-950/50 rounded-lg">
                        <div className="text-slate-200 text-xs">How it works:</div>
                        <ul className="text-sm text-slate-300 mt-2 space-y-1">
                          <li>• Activities grant SP in relevant skills</li>
                          <li>• Accumulate SP to level up skills</li>
                          <li>• Request training for focused growth</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                          <Medal className="w-5 h-5 text-purple-400" />
                        </div>
                        <h4 className="font-bold text-purple-400">Badges & Commendations</h4>
                      </div>
                      <p className="text-sm text-slate-300">
                        Special honors awarded for exceptional achievements, milestone completions, 
                        and exemplary service to the organization.
                      </p>
                      <div className="mt-4 p-3 bg-slate-950/50 rounded-lg">
                        <div className="text-slate-200 text-xs">Examples:</div>
                        <ul className="text-sm text-slate-300 mt-2 space-y-1">
                          <li>• First Responder (First mission)</li>
                          <li>• Guardian (Protect red lines)</li>
                          <li>• Visionary (Strategic contributions)</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="p-4 bg-slate-900 border border-slate-700 rounded-lg">
                  <h4 className="font-semibold text-slate-200 mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-indigo-400" />
                    Direct vs Indirect Tasks
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-amber-400 font-semibold mb-1">Direct Tasks → XP</div>
                      <p className="text-slate-400">
                        Work that directly advances mission objectives. Writing code, closing deals, 
                        delivering features, publishing content.
                      </p>
                    </div>
                    <div>
                      <div className="text-emerald-400 font-semibold mb-1">Indirect Tasks → EP</div>
                      <p className="text-slate-400">
                        Work that enables direct tasks. Research, planning, coordination, setup, 
                        unblocking others. EP converts to XP when linked task completes.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-r from-indigo-500/5 to-purple-500/5 border-indigo-500/30">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div>
                    <h4 className="text-slate-100 mb-2 text-lg font-bold">Your Mission</h4>
                    <p className="text-slate-300">
                      As an officer in COMPASS, you'll execute missions, discover objectives, complete activities, 
                      and continuously develop your skills. Each action contributes to your growth and the success 
                      of your team. The system tracks your progress and rewards your dedication with tangible 
                      advancement opportunities.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PROGRESSION TAB */}
          <TabsContent value="progression" className="space-y-6 mt-6">
            <Card className="bg-slate-950/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-slate-100 font-semibold tracking-tight leading-none flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  How You Level Up
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-slate-900 rounded-lg">
                    <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-indigo-400 font-bold">1</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-200 mb-2">Accept & Execute Missions</h4>
                      <p className="text-sm text-slate-400 mb-3">
                        Commanders assign you missions through the Mission Control system. Each mission 
                        has objectives that you'll work to complete.
                      </p>
                      <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                        Reward: 500-1000 XP per mission
                      </Badge>
                    </div>
                  </div>

                  <div className="flex items-center justify-center">
                    <ChevronRight className="w-6 h-6 text-slate-600" />
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-slate-900 rounded-lg">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-cyan-400 font-bold">2</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-200 mb-2">Complete Activities</h4>
                      <p className="text-sm text-slate-400 mb-3">
                        Break down objectives into daily activities. Each activity completion earns you 
                        XP and SP in relevant skills.
                      </p>
                      <div className="flex gap-2">
                        <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30">
                          XP: 50-200
                        </Badge>
                        <Badge variant="outline" className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
                          SP: 10-100
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-center">
                    <ChevronRight className="w-6 h-6 text-slate-600" />
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-slate-900 rounded-lg">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-400 font-bold">3</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-200 mb-2">Level Up & Earn Recognition</h4>
                      <p className="text-sm text-slate-400 mb-3">
                        Every 1000 XP you gain a level. Milestones earn you badges and unlock new 
                        responsibilities and access levels.
                      </p>
                      <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/30">
                        Unlock: New roles, badges, and permissions
                      </Badge>
                    </div>
                  </div>
                </div>

                <Card className="bg-amber-500/5 border-amber-500/30">
                  <CardContent className="pt-4">
                    <h4 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      Level Progression (Automatic)
                    </h4>
                    <p className="text-sm text-slate-300 mb-4">
                      Levels increase automatically every 1,000 XP. They represent your total experience and unlock access to higher-tier missions.
                    </p>
                    <div className="space-y-2">
                      {[
                      { level: 1, xp: '0' },
                      { level: 2, xp: '1,000' },
                      { level: 3, xp: '2,000' },
                      { level: 5, xp: '4,000' },
                      { level: 10, xp: '9,000' }].
                      map((lvl, idx) =>
                      <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-slate-800 last:border-0">
                          <Badge variant="outline" className="bg-slate-900 border-slate-700 text-slate-300 font-mono">
                            Level {lvl.level}
                          </Badge>
                          <span className="text-amber-400 font-mono">{lvl.xp} XP</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-purple-500/5 border-purple-500/30">
                  <CardContent className="pt-4">
                    <h4 className="font-semibold text-purple-400 mb-3 flex items-center gap-2">
                      <Crown className="w-5 h-5" />
                      Rank Advancement (Requires Approval)
                    </h4>
                    <p className="text-sm text-slate-300 mb-4">
                      Ranks are formal titles earned through demonstrated performance. Similar to military promotions, advancing requires:
                    </p>
                    <div className="space-y-3 mb-4">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="w-4 h-4 text-purple-400" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-200">Minimum XP Threshold</div>
                          <div className="text-xs text-slate-400">Required experience level met</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Trophy className="w-4 h-4 text-purple-400" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-200">Performance Record</div>
                          <div className="text-xs text-slate-400">Completed missions successfully</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Star className="w-4 h-4 text-purple-400" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-200">Skill Qualifications</div>
                          <div className="text-xs text-slate-400">Demonstrated proficiency in key skills</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Users className="w-4 h-4 text-purple-400" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-200">Commander Nomination</div>
                          <div className="text-xs text-slate-400">Formal recommendation from superior officer</div>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 bg-slate-900 rounded-lg space-y-2 text-xs">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-200">Cadet</span>
                        <span className="text-slate-200">Starting rank</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-200">Junior Officer</span>
                        <span className="text-slate-200">Level 3 + approval</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-200">Officer</span>
                        <span className="text-slate-200">Level 5 + 3 missions + approval</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-200">Senior Officer</span>
                        <span className="text-slate-200">Level 7 + leadership + approval</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-200">Captain</span>
                        <span className="text-slate-200">Level 10 + command experience + approval</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SKILLS TAB */}
          <TabsContent value="skills" className="space-y-6 mt-6">
            <Card className="bg-slate-950/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-slate-100 font-semibold tracking-tight leading-none flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-cyan-400" />
                  Skill Development System
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-slate-300">
                  Skills represent your proficiency in specific domains. Each skill has 5 levels, 
                  and you earn Skill Points (SP) by completing activities that require those skills.
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-slate-200">Skill Levels & SP Required</h4>
                    <div className="space-y-3">
                      {[
                      { level: 1, sp: 0, label: 'Novice', color: 'slate', benefit: 'Basic understanding' },
                      { level: 2, sp: 100, label: 'Beginner', color: 'blue', benefit: 'Can handle simple tasks' },
                      { level: 3, sp: 250, label: 'Intermediate', color: 'cyan', benefit: 'Work independently' },
                      { level: 4, sp: 500, label: 'Advanced', color: 'indigo', benefit: 'Lead others' },
                      { level: 5, sp: 1000, label: 'Expert', color: 'purple', benefit: 'Mastery & mentorship' }].
                      map((lvl) =>
                      <div key={lvl.level} className={`p-3 bg-${lvl.color}-500/5 border border-${lvl.color}-500/20 rounded-lg`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Star className={`w-4 h-4 text-${lvl.color}-400 fill-${lvl.color}-400`} />
                              <span className={`font-semibold text-${lvl.color}-400`}>Level {lvl.level}: {lvl.label}</span>
                            </div>
                            <span className="text-sm font-mono text-cyan-400">{lvl.sp} SP</span>
                          </div>
                          <p className="text-xs text-slate-400">{lvl.benefit}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-slate-200">How to Earn SP</h4>
                    
                    <Card className="bg-slate-900 border-slate-700">
                      <CardContent className="pt-4 space-y-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                            <span className="font-semibold text-slate-200">Activity Completion</span>
                          </div>
                          <p className="text-sm text-slate-400">
                            When you complete activities, the accountable officer allocates SP 
                            rewards to skills used in that work.
                          </p>
                        </div>

                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <GraduationCap className="w-4 h-4 text-cyan-400" />
                            <span className="font-semibold text-slate-200">Training Requests</span>
                          </div>
                          <p className="text-sm text-slate-400">
                            Request focused training from Commanders. Upon approval, you receive 
                            SP to advance specific skills.
                          </p>
                        </div>

                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Trophy className="w-4 h-4 text-amber-400" />
                            <span className="font-semibold text-slate-200">Skill Requirements</span>
                          </div>
                          <p className="text-sm text-slate-400">
                            Some activities have minimum skill requirements. Meeting these unlocks 
                            access to higher-value assignments.
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
                      <h5 className="font-semibold text-cyan-400 mb-2">Pro Tip</h5>
                      <p className="text-sm text-slate-300">
                        Focus on developing 2-3 core skills to expert level rather than spreading 
                        yourself thin. Deep expertise in key areas makes you invaluable to high-impact missions.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* RANKS & ROLES TAB */}
          <TabsContent value="ranks" className="space-y-6 mt-6">
            <Card className="bg-slate-950/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-slate-100 font-semibold tracking-tight leading-none flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-400" />
                  Understanding the Hierarchy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Zap className="w-5 h-5 text-amber-400" />
                        <h5 className="font-bold text-amber-400">Levels</h5>
                      </div>
                      <p className="text-sm text-slate-300 mb-2">
                        <strong>What:</strong> Numeric indicator of total experience
                      </p>
                      <p className="text-sm text-slate-300 mb-2">
                        <strong>How:</strong> Automatic every 1,000 XP
                      </p>
                      <p className="text-sm text-slate-300">
                        <strong>Purpose:</strong> Unlock mission tiers
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Crown className="w-5 h-5 text-purple-400" />
                        <h5 className="font-bold text-purple-400">Ranks</h5>
                      </div>
                      <p className="text-sm text-slate-300 mb-2">
                        <strong>What:</strong> Formal title (Cadet, Captain, etc.)
                      </p>
                      <p className="text-sm text-slate-300 mb-2">
                        <strong>How:</strong> Commander nomination + performance
                      </p>
                      <p className="text-sm text-slate-300">
                        <strong>Purpose:</strong> Recognition & prestige
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-indigo-500/10 to-blue-500/10 border-indigo-500/30">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Briefcase className="w-5 h-5 text-indigo-400" />
                        <h5 className="font-bold text-indigo-400">Roles</h5>
                      </div>
                      <p className="text-sm text-slate-300 mb-2">
                        <strong>What:</strong> Operational position & permissions
                      </p>
                      <p className="text-sm text-slate-300 mb-2">
                        <strong>How:</strong> Assigned by organization leadership
                      </p>
                      <p className="text-sm text-slate-300">
                        <strong>Purpose:</strong> Define responsibilities & access
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div className="p-4 bg-indigo-500/5 border border-indigo-500/20 rounded-lg">
                  <h4 className="font-semibold text-indigo-400 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    The Military Model
                  </h4>
                  <p className="text-sm text-slate-300">
                    Like real military organizations, COMPASS separates <strong>experience</strong> (levels), 
                    <strong>recognition</strong> (ranks), and <strong>responsibility</strong> (roles). You can be a 
                    high-level Operator with lots of XP but still need Commander approval to earn higher ranks. 
                    Similarly, your operational role is assigned based on organizational needs and trusted capabilities, 
                    not just personal advancement.
                  </p>
                </div>

                <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <h4 className="font-semibold text-amber-400 mb-2 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Mission Creation & Ownership
                  </h4>
                  <p className="text-sm text-slate-300">
                    <strong>Important:</strong> Only Commanders (Commander or Commander in Chief) can create new missions. 
                    Mission Owners and Sponsors must also be Commanders. This ensures strategic oversight and 
                    accountability at the highest level of mission planning. Task owners can edit their own tasks 
                    through the edit menu on each task card.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Operational Roles (RBAC)</h4>
                  <p className="text-sm text-slate-400 mb-4">
                    Roles define what you can do in the system. These are assigned by Commanders based on 
                    organizational structure, trust, and mission requirements—not automatically earned.
                    Only Commanders can create new missions and be assigned as mission Owners or Sponsors.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Commander in Chief */}
                    <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Crown className="w-5 h-5 text-amber-400" />
                          <h5 className="font-bold text-amber-400">👑 Commander in Chief</h5>
                        </div>
                        <p className="text-sm text-slate-300 mb-3">
                          Supreme access. All Commander permissions plus RBAC management, 
                          system configuration, and role assignment override.
                        </p>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>All system features</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>RBAC permission management</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Create missions & assign ownership</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Commander */}
                    <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Crown className="w-5 h-5 text-purple-400" />
                          <h5 className="font-bold text-purple-400">Commander</h5>
                        </div>
                        <p className="text-sm text-slate-300 mb-3">
                          Full strategic access. Design missions, approve objectives, manage officers, 
                          review training requests, and oversee all operations.
                        </p>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Create missions & assign ownership</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Manage frameworks & codex</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Approve training & skills</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Senior Officer */}
                    <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Shield className="w-5 h-5 text-emerald-400" />
                          <h5 className="font-bold text-emerald-400">Senior Officer</h5>
                        </div>
                        <p className="text-sm text-slate-300 mb-3">
                          Officer access plus Org Chart visibility. Can view organizational structure 
                          and manage missions, objectives, and activities.
                        </p>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Org Chart visibility</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Mission & objective management</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Activity execution</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Officer */}
                    <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/30">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Briefcase className="w-5 h-5 text-blue-400" />
                          <h5 className="font-bold text-blue-400">Officer</h5>
                        </div>
                        <p className="text-sm text-slate-300 mb-3">
                          Manage objectives and activities. Create activities, assign responsibilities, 
                          allocate SP rewards, and track execution.
                        </p>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Objective & activity management</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Create & assign activities</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Allocate skill rewards</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Analyst */}
                    <Card className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 border-cyan-500/30">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Target className="w-5 h-5 text-cyan-400" />
                          <h5 className="font-bold text-cyan-400">Analyst</h5>
                        </div>
                        <p className="text-sm text-slate-300 mb-3">
                          View and analyze operations. Access strategic frameworks, policies, and 
                          capabilities. Contribute insights but limited execution rights.
                        </p>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>View framework & analysis</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Discovery & objectives</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Policy & capability review</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Operator */}
                    <Card className="bg-gradient-to-br from-slate-500/10 to-slate-600/10 border-slate-500/30">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Users className="w-5 h-5 text-slate-400" />
                          <h5 className="font-bold text-slate-400">Operator</h5>
                        </div>
                        <p className="text-sm text-slate-300 mb-3">
                          Execute assigned missions and activities. Focus on daily task completion 
                          and skill development through hands-on work.
                        </p>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Execute missions & activities</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Daily task management</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Request skill training</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* AI Officer */}
                    <Card className="bg-gradient-to-br from-indigo-500/10 to-violet-500/10 border-indigo-500/30 md:col-span-2">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-5 h-5 text-indigo-400" />
                          <h5 className="font-bold text-indigo-400">🤖 AI Officer</h5>
                        </div>
                        <p className="text-sm text-slate-300 mb-3">
                          AI Co-workers that automate task execution. Requires a human owner and 
                          Commander approval (unless created by a Commander).
                        </p>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Task execution & automation</span>
                          </div>
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Bot-driven workflows</span>
                          </div>
                          <div className="flex items-center gap-2 text-amber-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Requires human owner supervision</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Rank Advancement Process</h4>
                  <p className="text-sm text-slate-400 mb-4">
                    Inspired by military promotion boards, rank advancement requires a formal process:
                  </p>
                  
                  <div className="space-y-3">
                    <Card className="bg-slate-900 border-slate-700">
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-indigo-400 font-bold">1</span>
                          </div>
                          <div className="flex-1">
                            <h5 className="font-semibold text-slate-200 mb-1">Meet Minimum Requirements</h5>
                            <p className="text-sm text-slate-400">
                              Achieve required level, complete minimum missions, demonstrate key skills. 
                              XP alone doesn't qualify you—performance matters.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-slate-900 border-slate-700">
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-purple-400 font-bold">2</span>
                          </div>
                          <div className="flex-1">
                            <h5 className="font-semibold text-slate-200 mb-1">Commander Nomination</h5>
                            <p className="text-sm text-slate-400">
                              A superior officer reviews your record and formally nominates you for advancement. 
                              They assess leadership, reliability, and mission impact.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-slate-900 border-slate-700">
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-emerald-400 font-bold">3</span>
                          </div>
                          <div className="flex-1">
                            <h5 className="font-semibold text-slate-200 mb-1">Promotion Granted</h5>
                            <p className="text-sm text-slate-400">
                              Once approved, your rank title is updated in your profile. This unlocks new badges, 
                              recognition, and potential for role advancement.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Clearance Levels</h4>
                  <p className="text-sm text-slate-400 mb-4">
                    Clearance levels (1-10) determine access to sensitive information and high-stakes 
                    missions. Granted by Commanders based on trust, performance, and need-to-know.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="bg-slate-950 border-slate-700 font-mono">Level 1-3</Badge>
                        <span className="text-slate-300">Standard Operations</span>
                      </div>
                      <span className="text-xs text-slate-500">Entry level missions</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="bg-blue-950 border-blue-700 text-blue-400 font-mono">Level 4-6</Badge>
                        <span className="text-slate-300">Strategic Operations</span>
                      </div>
                      <span className="text-xs text-slate-500">Campaign access</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="bg-purple-950 border-purple-700 text-purple-400 font-mono">Level 7-10</Badge>
                        <span className="text-slate-300">Special Operations</span>
                      </div>
                      <span className="text-xs text-slate-500">Critical missions only</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SIMULATION TAB */}
          <TabsContent value="simulation" className="space-y-6 mt-6">
            <Card className="bg-slate-950/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-slate-100 font-semibold tracking-tight leading-none flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-emerald-400" />
                  Interactive Simulation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-slate-300">
                  Try out the progression system! Click the buttons below to simulate completing activities 
                  and leveling up. Watch how your XP, SP, and level increase.
                </p>

                {/* Simulated Officer Card */}
                <div className="p-6 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-xl">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h4 className="text-2xl font-bold text-slate-100">Officer Simulation</h4>
                      <p className="text-sm text-slate-400">Your test officer profile</p>
                    </div>
                    <Button data-testid="profile-reset-btn" size="sm" variant="outline" onClick={resetSimulation}>
                      Reset
                    </Button>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                      <div className="text-xs text-slate-500 mb-1">Level</div>
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={simulatedLevel}
                          initial={{ scale: 1.5, color: '#fbbf24' }}
                          animate={{ scale: 1, color: '#f8fafc' }}
                          className="text-3xl font-bold">

                          {simulatedLevel}
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                      <div className="text-xs text-slate-500 mb-1">Total XP</div>
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={simulatedXP}
                          initial={{ scale: 1.2, color: '#fbbf24' }}
                          animate={{ scale: 1, color: '#fbbf24' }}
                          className="text-3xl font-bold font-mono text-amber-400">

                          {simulatedXP}
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                      <div className="text-xs text-slate-500 mb-1">Skill Points</div>
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={simulatedSP}
                          initial={{ scale: 1.2, color: '#22d3ee' }}
                          animate={{ scale: 1, color: '#22d3ee' }}
                          className="text-3xl font-bold font-mono text-cyan-400">

                          {simulatedSP}
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  </div>

                  <div className="space-y-2 mb-6">
                    <div className="flex justify-between text-xs text-slate-400">
                      <span>Progress to Level {simulatedLevel + 1}</span>
                      <span>{currentLevelXP} / 1000 XP</span>
                    </div>
                    <Progress value={progressPercent} className="h-3" indicatorClassName="bg-amber-400" />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <Button
                      onClick={simulateActivityCompletion}
                      disabled={activeSimulation !== null}
                      className="bg-emerald-600 hover:bg-emerald-700 h-auto py-4 flex-col items-start">

                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-bold">Complete Activity</span>
                      </div>
                      <span className="text-xs opacity-80">+500 XP • +50 SP</span>
                    </Button>

                    <Button
                      onClick={simulateLevelUp}
                      disabled={activeSimulation !== null}
                      className="bg-purple-600 hover:bg-purple-700 h-auto py-4 flex-col items-start">

                      <div className="flex items-center gap-2 mb-1">
                        <Trophy className="w-5 h-5" />
                        <span className="font-bold">Level Up!</span>
                      </div>
                      <span className="text-xs opacity-80">Advance to next level</span>
                    </Button>
                  </div>

                  {activeSimulation &&
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-4 bg-emerald-500/20 border border-emerald-500/30 rounded-lg">

                      <div className="flex items-center gap-2 text-emerald-400">
                        <Sparkles className="w-5 h-5" />
                        <span className="font-semibold">
                          {activeSimulation === 'activity' ? 'Activity Completed!' : 'Level Up!'}
                        </span>
                      </div>
                    </motion.div>
                  }
                </div>

                <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                  <h5 className="font-semibold text-blue-400 mb-2">What's Happening?</h5>
                  <ul className="text-sm text-slate-300 space-y-2">
                    <li>• <strong>Complete Activity:</strong> Simulates finishing work and earning rewards</li>
                    <li>• <strong>Level Up:</strong> Increases your officer level, unlocking new capabilities</li>
                    <li>• <strong>XP Progress Bar:</strong> Shows how close you are to the next level (1000 XP each)</li>
                    <li>• <strong>Skill Points:</strong> Accumulate to advance specific skill proficiencies</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-6 p-4 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-lg">
          <div className="flex items-start gap-3">
            <Award className="w-6 h-6 text-indigo-400 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-indigo-400 mb-1">Ready to Begin Your Journey?</h4>
              <p className="text-sm text-slate-300">
                Head to Mission Control to accept your first assignment, or visit the Activities page 
                to see what work awaits. Every action you take contributes to your growth as an officer. 
                Good hunting! 🎯
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>);

}